/**
 * The chat jurisdiction picker's contract (MAR-1418).
 *
 * An organization chats in exactly ONE jurisdiction per conversation, chosen from
 * `{main_jurisdiction} ∪ additional_jurisdictions` (or, for a `'global'` org, from every
 * jurisdiction the corpus carries). The picker renders only when there is more than one to choose.
 */

/** `GET /api/chat/jurisdictions`. */
export interface ChatJurisdictionOptions {
  /** What a new chat starts on: the org main, or `SV` for a `'global'` org. */
  default_jurisdiction: string
  /** ISO 3166-1 alpha-2 codes, main first. One entry (or none) means no picker. */
  jurisdictions: string[]
}

/** The most additional jurisdictions an organization may list. A guard, not a product limit. */
export const MAX_ADDITIONAL_JURISDICTIONS = 20

/** Whether the composer should offer a jurisdiction choice at all (AC2, AC5). */
export function showsJurisdictionPicker(options: readonly string[] | null | undefined): boolean {
  return (options?.length ?? 0) > 1
}

/** One checkbox of the "additional jurisdictions" multi-select (org settings, superadmin card). */
export interface AdditionalJurisdictionCandidate {
  value: string
  /**
   * Stored on the org but no longer loaded in the current corpus. Shown so it can be unchecked —
   * the server keeps or removes a stored code without re-checking it — but it cannot be re-added
   * once removed (MAR-1418 audit).
   */
  stale: boolean
}

/**
 * The multi-select's checkboxes: every LOADED code except main (from `/api/orgs/legal-jurisdictions`
 * or `/api/admin/legal-jurisdictions`, the corpus the save checks), then any STORED code that is no
 * longer loaded, marked `stale`.
 */
export function additionalJurisdictionCandidates(
  loaded: readonly string[],
  stored: readonly string[] | null | undefined,
  main: string | null | undefined,
): AdditionalJurisdictionCandidate[] {
  const out: AdditionalJurisdictionCandidate[] = loaded
    .filter(code => code !== main)
    .map(code => ({ value: code, stale: false }))
  for (const code of stored ?? []) {
    if (code !== main && !loaded.includes(code)) out.push({ value: code, stale: true })
  }
  return out
}
